/**
 * Locations finder search + geolocation (ported from tmp/map.ts and related components).
 * Uses URL query params: ?search= | ?lat=&lng= | ?geoError=1 (search takes precedence over geo).
 */

const MAP_ID = 'locations-map';

function getLocationsBasePath() {
  const form = document.getElementById('proximity-search');
  if (form?.action) {
    try {
      return new URL(form.action, window.location.origin).pathname;
    } catch {
      /* fall through */
    }
  }
  return window.location.pathname;
}

function navigateLocations(params) {
  const base = getLocationsBasePath();
  const query = params.toString();
  window.location.href = query ? `${base}?${query}` : base;
}

function clearGeoCookies() {
  document.cookie = 'vlat=; path=/; max-age=0';
  document.cookie = 'vlon=; path=/; max-age=0';
  document.cookie = 'geoError=; path=/; max-age=0';
}

/** useSearch().goTo — min 5 chars, clears lat/lng */
function goToSearch(search) {
  const term = (search ?? '').trim();
  if (term.length < 5) {
    return false;
  }
  clearGeoCookies();
  const params = new URLSearchParams();
  params.set('search', term);
  navigateLocations(params);
  return true;
}

/** useSearch().clearSearch */
function clearSearch() {
  clearGeoCookies();
  navigateLocations(new URLSearchParams());
}

/** useCurrentLocation().goTo — clears address search from URL */
function goToCurrentLocation(lat, lng) {
  clearGeoCookies();
  const maxAge = 86400;
  document.cookie = `vlat=${lat}; path=/; max-age=${maxAge}`;
  document.cookie = `vlon=${lng}; path=/; max-age=${maxAge}`;
  const params = new URLSearchParams();
  params.set('lat', lat);
  params.set('lng', lng);
  navigateLocations(params);
}

/** useCurrentLocation().clearCur */
function clearCurrentLocation() {
  clearGeoCookies();
  navigateLocations(new URLSearchParams());
}

function geoSuccess(pos) {
  goToCurrentLocation(
    pos.coords.latitude.toString(),
    pos.coords.longitude.toString(),
  );
}

function geoError(err) {
  console.warn(`ERROR(${err.code}): ${err.message}`);
  clearGeoCookies();
  const params = new URLSearchParams();
  params.set('geoError', '1');
  navigateLocations(params);
}

/** currentLocationBtn toggle: clear if lat/lng already in URL, else geolocate */
function locButtonHandler() {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('lat') && urlParams.get('lng')) {
    clearCurrentLocation();
    return;
  }

  const rawCookies = document.cookie.split(';').map((c) => c.trim());
  const hasCookieGeo = rawCookies.some((c) => c.startsWith('vlat=')) && rawCookies.some((c) => c.startsWith('vlon='));
  if (hasCookieGeo && !urlParams.get('search')) {
    clearCurrentLocation();
    return;
  }

  if (!navigator.geolocation) {
    geoError({
      code: 0,
      message: 'Geolocation is not supported by this browser.',
    });
    return;
  }

  navigator.geolocation.getCurrentPosition(geoSuccess, geoError, {
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0,
  });
}

function mapPan(markerId) {
  if (typeof googleMaps === 'undefined') {
    return;
  }
  const map = googleMaps.getMap(MAP_ID);
  map.zoom(12);
  map.panToMarker(markerId);
  map.closeInfoWindow('*');
  map.openInfoWindow(markerId);
}

/**
 * Google Maps plugin _preventGreyBox() can reset the map to 0,0 after DNA center/zoom.
 * Use the native Maps API so we don't re-trigger plugin center() logging/parsing bugs.
 */
function refineLocationsMapView() {
  if (typeof googleMaps === 'undefined' || typeof google === 'undefined') {
    return;
  }

  const mapObj = googleMaps.getMap(MAP_ID, true);
  const gMap = mapObj?._map;
  if (!gMap) {
    return;
  }

  const markerIds = Object.keys(mapObj._markers ?? {});
  if (markerIds.length !== 1) {
    return;
  }

  const marker = mapObj._markers[markerIds[0]];
  const position = marker?.getPosition?.();
  if (!position) {
    return;
  }

  const current = gMap.getCenter();
  const atNullIsland =
    current &&
    Math.abs(current.lat()) < 0.01 &&
    Math.abs(current.lng()) < 0.01;

  const listItems = document.querySelectorAll('.sidebar-list .loc-item');
  if (listItems.length !== 1 && !atNullIsland) {
    return;
  }

  const root = document.querySelector('.map-component');
  const visitorLat = parseFloat(root?.dataset?.visitorLat ?? '');
  const visitorLng = parseFloat(root?.dataset?.visitorLng ?? '');

  const bounds = new google.maps.LatLngBounds();
  bounds.extend(position);

  const hasVisitor =
    !Number.isNaN(visitorLat) &&
    !Number.isNaN(visitorLng) &&
    (Math.abs(visitorLat) > 0.01 || Math.abs(visitorLng) > 0.01);

  if (hasVisitor) {
    bounds.extend({ lat: visitorLat, lng: visitorLng });
  }

  if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
    gMap.setCenter({ lat: position.lat(), lng: position.lng() });
    gMap.setZoom(12);
  } else {
    gMap.fitBounds(bounds);
  }
}

let refineLocationsMapTimeout;

function scheduleRefineLocationsMapView() {
  window.clearTimeout(refineLocationsMapTimeout);
  refineLocationsMapTimeout = window.setTimeout(refineLocationsMapView, 250);
}

function bindProximitySearchForm() {
  const form = document.getElementById('proximity-search');
  if (!form || form.dataset.ccsLocationsBound === '1') {
    return;
  }
  form.dataset.ccsLocationsBound = '1';

  form.addEventListener('submit', (event) => {
    const input = form.querySelector('[name="search"]');
    const term = (input?.value ?? '').trim();
    if (term.length < 5) {
      event.preventDefault();
      return;
    }
    clearGeoCookies();
  });
}

function onLocationsMapAfterSwap(event) {
  const target = event.detail?.target;
  if (target?.querySelector?.(`#${MAP_ID}, .map-component`)) {
    scheduleRefineLocationsMapView();
  }
}

function registerLocationsSearchGlobals() {
  window.ccsGoToSearch = goToSearch;
  window.ccsClearSearch = clearSearch;
  window.ccsGoToCurrentLocation = goToCurrentLocation;
  window.ccsClearCurrentLocation = clearCurrentLocation;
  window.ccsGeoSuccess = geoSuccess;
  window.ccsGeoError = geoError;
  window.ccsLocButtonHandler = locButtonHandler;
  window.mapPan = mapPan;
  window.ccsScheduleRefineLocationsMapView = scheduleRefineLocationsMapView;
}

registerLocationsSearchGlobals();

document.addEventListener('DOMContentLoaded', bindProximitySearchForm);
document.addEventListener('htmx:afterSwap', bindProximitySearchForm);
document.addEventListener('htmx:afterSwap', onLocationsMapAfterSwap);
